/* eslint-disable react/jsx-props-no-spreading */
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {useEffect, useState} from 'react';
import {checkPermission, formatMoney} from 'utils/utils';
import {apiStatus, defaultpageCount, integrationVendors, permissionKeys, routePaths, sortByTypeType} from 'utils/constants';
import {useSelector} from 'react-redux';
import {locationSelector, orgSelector, userSelector} from 'redux/selectors';
import useUser from 'hooks/useUser';
import ButtonBack from 'components/button/buttonBack';
import Buttons from 'components/button/Buttons';
import {Label, Table, TextInput, Tooltip} from 'flowbite-react';
import {createPurchaseOrder, deletePurchaseOrderById, getPurchaseOrderById, updatePurchaseOrderById} from 'api/purchaseOrderApi';
import { getOrganizationIntegrationConfig } from 'api/organizationApi';
import {getItemById, getItemsByCategory, getItemsByFilter} from 'api/itemApi';
import { getOrgVendorsByFilter } from 'api/vendorApi';
import EmptyState from 'components/commonComponent/emptyState';
import {v4 as uuidv4} from 'uuid';
import {purchaseOrderItemType} from 'utils/proptypes';
import MultiSelect from 'components/select/index';
import {cloneDeep, concat, isEmpty, orderBy, sumBy} from 'lodash';
import GroupButtonWithIcons from 'components/button/buttonGroupWithIcons';
import {HiOutlineExclamationCircle, HiOutlineTrash} from 'react-icons/hi';
import {toast} from 'react-toastify';
import './purchaseOrder.scss';
import CreateItemByCategoryModal from 'components/modal/purchaseOrderModal/createItemByCategoryModal';
import {initOrder} from 'utils/initData';
import {getLocationById} from 'api/locationApi';
import axios from 'axios';
import urls from 'api/urls';
import StatisticsSaleModal from 'components/modal/purchaseOrderModal/statisticsSaleModal';
import {getStockByItemAndLocation, statisticsSaleItem} from 'api/stockApi';
import Sort from 'components/table/sort';

type orderN2GProps = {
    order: any
};

const PurchaseOrderDetail = (props: any) => {
    const {type} = props;
    const {WRITE_PURCHASE_ORDER, PLATFORM_ADMIN} = permissionKeys;
    const location = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const params: any = useParams();
    const [data, setData] = useState<any[]>([]);
    const [itemTypeOptions, setItemTypeOptions] = useState<any[]>([{label: '...', value: null}]);
    const {userInfo} = useSelector(userSelector);
    const {organizationId} = useSelector(orgSelector);
    const {locationId} = useSelector(locationSelector);
    const {fetchCurrentUser} = useUser();
    const [searchValue, setSearchValue] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [pageCount, setPageCount] = useState(defaultpageCount);
    const [totalEntities, setTotalEntities] = useState(0);
    const [t] = useTranslation();
    const [sortBy, setSortBy] = useState('');
    const [sortByType, setSortByType] = useState('');
    const isWriteOrg = checkPermission(userInfo, props.type, [WRITE_PURCHASE_ORDER], organizationId);
    const itemEmpty = require('../../assets/image/svg/Emptys.svg').default || '';
    const [openModal, setOpenModal] = useState(false);
    const [openModalStatistics, setOpenModalStatistics] = useState(false);
    const [category, setCategory] = useState([]);
    const [targetPurchaseOrder, setTargetPurchaseOrder] = useState(initOrder);
    const [dataChart, setDataChart] = useState<any>(null);
    const [itemNameChart, setItemNameChart] = useState('');
    const [errorsN2G, setErrorsN2G] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [orderTotalNew, setOrderTotalNew] = useState<number>(0);
    const [totalItem, setTotalItem] = useState<number>(0);
    const pageCountDefault = 1000;
    const organizationVendorId = new URLSearchParams(location.search).get('organizationVendorId') || '';
    const shouldAutoAddItemRow = !!(location.state as any)?.autoAddItemRow;
    const [hasInitializedBlankOrderRow, setHasInitializedBlankOrderRow] = useState(false);
    const [selectedVendorIntegration, setSelectedVendorIntegration] = useState<string>('');
    const [selectedVendorName, setSelectedVendorName] = useState<string>('');
    const [selectedVendorId, setSelectedVendorId] = useState<string>('');
    const isN2GIntegration = selectedVendorIntegration === integrationVendors.N2G_MLVEDA;
    const isDotfitIntegration = selectedVendorIntegration === integrationVendors.DOTFIT;
    const resolveVendorNameByOrgVendorId = (targetOrganizationVendorId?: string) => {
        if (!targetOrganizationVendorId) {
            setSelectedVendorIntegration('');
            setSelectedVendorName('');
            setSelectedVendorId('');
            return;
        }
        getOrgVendorsByFilter({page: 0, limit: 1000, sortByType: sortByTypeType.DESC, organizationId}).then((res: any) => {
            const orgVendor = res?.data?.entities?.find((v: any) => v.id === targetOrganizationVendorId);
            setSelectedVendorIntegration(orgVendor?.integration || '');
            setSelectedVendorName(orgVendor?.name || '');
            setSelectedVendorId(orgVendor?.vendorId || '');
        }).catch(() => {
            setSelectedVendorIntegration('');
            setSelectedVendorName('');
            setSelectedVendorId('');
        });
    };
    const filterItemsBySelectedVendor = (items: any[] = []) => {
        if (!organizationVendorId) return items;
        return items.filter((item: any) => item?.organizationVendorId === organizationVendorId);
    };

    const { data: n2gIntegrationData } = useQuery(
        ['getOrganizationIntegrationConfig', organizationId, integrationVendors.N2G_MLVEDA],
        () => getOrganizationIntegrationConfig(organizationId, integrationVendors.N2G_MLVEDA),
        { enabled: !!organizationId && isN2GIntegration },
    );

    const n2gBaseUrl = n2gIntegrationData?.data?.siteUrl || process.env.REACT_APP_N2G_MLVEDA_API_URL || '';
    const n2gValidateUrl = n2gBaseUrl ? `${n2gBaseUrl}/order/validateOrder/` : urls.purchaseOrder.validateOrderN2G;

    useEffect(() => {
        if (!organizationVendorId) {
            setSelectedVendorIntegration('');
            setSelectedVendorName('');
            setSelectedVendorId('');
            return;
        }
        getOrgVendorsByFilter({page: 0, limit: 1000, sortByType: sortByTypeType.DESC, organizationId}).then((res: any) => {
            const orgVendor = res?.data?.entities?.find((v: any) => v.id === organizationVendorId);
            setSelectedVendorIntegration(orgVendor?.integration || '');
            setSelectedVendorName(orgVendor?.name || '');
            setSelectedVendorId(orgVendor?.vendorId || '');
        }).catch(() => {
            setSelectedVendorIntegration('');
            setSelectedVendorName('');
            setSelectedVendorId('');
        });
    }, [organizationVendorId, organizationId]);

    useEffect(() => {
        if (params.id !== 'blank-new') {
            setIsLoading(true);
            getPurchaseOrderById(params?.id).then((res: any) => {
                if (!organizationVendorId) {
                    const orderOrganizationVendorId = res?.data?.organizationVendorId || res?.data?.purchaseOrderItems?.[0]?.item?.organizationVendorId;
                    resolveVendorNameByOrgVendorId(orderOrganizationVendorId);
                }
                setTargetPurchaseOrder(res.data);
                setTotalItem(res.data?.purchaseOrderItems?.length);
                if (data.length === 0) {
                    setData(
                        res.data?.purchaseOrderItems?.map((item: any) => {
                            return {
                                id: item.id,
                                itemId: item.itemId,
                                item: item.item,
                                orderCount: item.quantityOrdered,
                                quantityOnHand: item.quantityOnHand,
                                quantityOnOrder: item.quantityOnOrder ?? 0,
                                unitSoldLast7: item.unitSoldLast7,
                                sixWeekAvg: item.sixWeekAvg,
                                minimumStock: item.minimumStock ?? null,
                            };
                        }),
                    );
                }
                if (data?.length !==  res.data?.purchaseOrderItems?.length) {
                    handleSortData();
                } else {
                    setData(
                        res.data?.purchaseOrderItems?.map((item: any) => {
                            return {
                                id: item.id,
                                itemId: item.itemId,
                                item: item.item,
                                orderCount: item.quantityOrdered,
                                quantityOnHand: item.quantityOnHand,
                                quantityOnOrder: item.quantityOnOrder ?? 0,
                                unitSoldLast7: item.unitSoldLast7,
                                sixWeekAvg: item.sixWeekAvg,
                                minimumStock: item.minimumStock ?? null,
                            };
                        }),
                    );
                    handleSortData();
                }
                if (isN2GIntegration && (res.data?.status === 'Draft' || res.data?.status === 'Requested')) {
                    //validate data to n2g
                    getLocationAndConvertLocationN2G(locationId).then((resLocation: any) => { 
                        const validateData: orderN2GProps = {
                            order: {
                                customer: getCustomerN2GWhenLoading(res.data),
                                location: resLocation,
                                items: convertItemToItemN2G(res.data?.purchaseOrderItems)
                            }
                        };
                        if (validateData.order?.location) {
                            const url = n2gValidateUrl;
                            axios.post(url, validateData, {headers: {'Access-Control-Allow-Origin': url, 'Accept': '*/*'}})
                                .then(response => {
                                    setErrorsN2G(null);
                                    setIsLoading(false);
                                })
                                .catch(error => {
                                    if (error?.response?.data?.errors || error?.response?.data?.location) {
                                        const errorsItems: any = [];
                                        for (let index = 0; index < error.response.data?.errors?.length; index++) {
                                            const element = error.response.data?.errors[index];
                                            for (let index = 0; index < element.errors?.length; index++) {
                                                const elementMsg = element.errors[index];
                                                elementMsg.sku = element.sku;
                                                if (element.quantityAvailable !== undefined) {
                                                    elementMsg.quantityAvailable = element.quantityAvailable;
                                                }
                                                errorsItems.push(elementMsg);
                                            }
                                        }
                                        const errorsLocation = error.response.data?.location?.errors;
                                        const errorsCustomer = error.response.data?.customer?.errors;
                                        const errorsList: any = {
                                            location: errorsLocation,
                                            items: errorsItems,
                                            customer: errorsCustomer
                                        }
                                        setErrorsN2G(errorsList);
                                        setIsLoading(false);
                                    }
                                });
                        }
                    });
                } else {
                    setIsLoading(false);
                }
            });
        } else {
            if (data?.length > 0) {
                handleSortData();
            } else {
                setData([]);
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params?.id, locationId, sortBy, sortByType, isN2GIntegration, organizationVendorId]);

    const handleSortData = () => {
        if (sortBy && sortByType) {
            const sortType = sortByType === sortByTypeType.ASC ? 'asc' : 'desc';
            let sortedList: any = [];
            switch (sortBy) {
                case 'itemName':
                    sortedList = orderBy(data, ['item.name'], sortType);
                    break;
                case 'itemSku':
                    sortedList = orderBy(data, ['item.sku'], sortType);
                    break;
                case 'quantityOrdered':
                    sortedList = orderBy(data, ['orderCount'], sortType);
                    break;
                case 'itemCaseCost':
                    sortedList = orderBy(data, ['item.caseCost'], sortType);
                    break;
            }
            setData(sortedList);
        }
    }
	
	
	const handleHeaderSort = (field: string) => {
        setSortBy(field);
        let type = '';
        if (field !== sortBy) {
            type = sortByTypeType.ASC;
        } else if (sortByType === sortByTypeType.ASC) {
            type = sortByTypeType.DESC;
        } else {
            setSortBy(type);
        }
        setSortByType(type);
    };

    useEffect(() => {
        if (category.length > 0) {
            let ids: string = '';
            for (let index = 0; index < category.length; index++) {
                const id = category[index];
                if (ids === '') {
                    ids = id;
                } else {
                    ids = `${ids},${id}`;
                }
            }
            getItemsByCategory({categoryIds: ids, locationId, organizationVendorId}).then((res: any) => {
                if (res.data.totalEntities > 0) {
                    // Filter to only include active items for purchase orders
                    const activeItems = filterItemsBySelectedVendor(res.data.entities).filter((item: any) => item.isActive !== false);
                    
                    if (data && data?.length > 0) {
                        const mergedItems = [...data,...activeItems.map((item: any) => {
                            return {id: uuidv4(), itemId: item.id, item: item, orderCount: 1, quantityOnHand: item.quantityOnHand, quantityOnOrder: item.quantityOnOrder ?? 0, unitSoldLast7: item.unitSoldLast7, sixWeekAvg: item.sixWeekAvg, minimumStock: item.minimumStock ?? null};
                        })];
                        const uniqueItems = mergedItems.reduce((acc, item) => {
                            if (!acc.some((existingItem: any) => existingItem.itemId === item.itemId)) {
                              acc.push(item);
                            }
                            return acc;
                          }, []).filter((f: any) => f.item !== null);
                          setData(uniqueItems);
                          validateOrderN2G(locationId, uniqueItems);
                          calculationTotal(uniqueItems);
                    } else {
                        setData(
                            activeItems.map((item: any) => {
                                return {id: uuidv4(), itemId: item.id, item: item, orderCount: 1, quantityOnHand: item.quantityOnHand, quantityOnOrder: item.quantityOnOrder ?? 0, unitSoldLast7: item.unitSoldLast7, sixWeekAvg: item.sixWeekAvg, minimumStock: item.minimumStock ?? null};
                            }),
                        );
                        validateOrderN2G(locationId, data);
                        calculationTotal(activeItems.map((item: any) => {
                            return {id: uuidv4(), itemId: item.id, item: item, orderCount: 1, quantityOnHand: item.quantityOnHand, quantityOnOrder: item.quantityOnOrder ?? 0, unitSoldLast7: item.unitSoldLast7, sixWeekAvg: item.sixWeekAvg, minimumStock: item.minimumStock ?? null};
                        }));
                    }
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [category]);

    const validateOrderN2G = (locationId: string, request: any) => {
        if (!isN2GIntegration) {
            setErrorsN2G(null);
            return;
        }
        //validate data to n2g
        getLocationAndConvertLocationN2G(locationId).then((resLocation: any) => { 
            const validateData: orderN2GProps = {
                order: {
                    customer: getCustomerN2G(),
                    location: resLocation,
                    items: convertItemToItemN2G(request)
                }
            };
            if (validateData.order?.location) {
                const url = n2gValidateUrl;
                axios.post(url, validateData, {headers: {'Access-Control-Allow-Origin': url, 'Accept': '*/*'}})
                    .then(response => {
                        setErrorsN2G(null);
                    })
                    .catch(error => {
                        if (error?.response?.data?.errors || error?.response?.data?.location) {
                            const errorsItems: any = [];
                            for (let index = 0; index < error.response.data?.errors?.length; index++) {
                                const element = error.response.data?.errors[index];
                                for (let index = 0; index < element.errors?.length; index++) {
                                    const elementMsg = element.errors[index];
                                    elementMsg.sku = element.sku;
                                    if (element.quantityAvailable !== undefined) {
                                        elementMsg.quantityAvailable = element.quantityAvailable;
                                    }
                                    errorsItems.push(elementMsg);
                                }
                            }
                            const errorsLocation = error.response.data?.location?.errors;
                            const errorsCustomer = error.response.data?.customer?.errors;
                            const errorsList: any = {
                                location: errorsLocation,
                                items: errorsItems,
                                customer: errorsCustomer
                            }
                            setErrorsN2G(errorsList);
                        }
                    });
            }
        });
    }

    const calculationTotal = (requestData: any) => {
        let sum = 0;
        for (let index = 0; index < requestData?.length; index++) {
            const element: any = requestData[index];
            sum = sum + ((element.orderCount || 0) * (element.item?.caseCost || 0));
        }
        if (params?.id !== 'blank-new') {
            targetPurchaseOrder.orderTotal = sum;
        } else {
            
            setOrderTotalNew(sum);
        }
        setTotalItem(requestData?.length);
    }

    const fetchItems = async () => {
        if (!params?.id) return;
    
        let listTemps = [];
    
        try {
          const filterParams = {
            limit: pageCountDefault,
            sortByType: sortByTypeType.DESC,
            organizationId,
            locationId,
            organizationVendorId,
            vendorId: selectedVendorId,
            isActive: true, // Always filter to active items only for purchase orders
          };
    
          const res = await getItemsByFilter({ ...filterParams, page: 0 });
          const totalCount = res?.data?.totalEntities || 0;
    
          if (totalCount === 0) {
            setItemTypeOptions([]);
            return;
          }
    
          listTemps = filterItemsBySelectedVendor(res?.data?.entities || []);
    
          const pageBreak = Math.ceil(totalCount / pageCountDefault);
    
          for (let index = 1; index < pageBreak; index++) {
            const resPage = await getItemsByFilter({ ...filterParams, page: index });
            listTemps = [...listTemps, ...filterItemsBySelectedVendor(resPage?.data?.entities || [])];
          }
    
          const newOptions = listTemps.map((item: any) => ({
            label: item.name,
            value: { id: item.id, upcs: item.upcs },
          }));
    
          setItemTypeOptions(newOptions);
        } catch (error) {
          console.error('Error fetching items:', error);
          setItemTypeOptions([]);
        } finally {
          
        }
      };

    useEffect(() => {
        fetchItems();

        // eslint-disable-next-line
    }, [params?.id, organizationVendorId, selectedVendorId]);

    useEffect(() => {
        if (!isN2GIntegration) return;
        if (errorsN2G?.location && errorsN2G?.location?.length > 0) {
            toast.error(`Location Error: ${buildErrorMessageN2G(errorsN2G.location)}`);
        }
        if (errorsN2G?.customer && errorsN2G?.customer?.length > 0) {
            toast.error(`Customer Error: ${buildErrorMessageN2G(errorsN2G.customer)}`);
        }
        // eslint-disable-next-line
    }, [errorsN2G, isN2GIntegration]);

    const handleSubmitPurchaseOrder = async (items: any) => {
        if (hasDuplicateItemInRows(items)) {
            const message: string = t('orderPage.duplicateLineItemNotAllowed');
            toast.error(message);
            return;
        }
        let check = true;
        for (let index = 0; index < items.length; index++) {
            const rowItem: any = items[index];
            if (rowItem) {
                if (rowItem.itemId === '') {
                    check = false;
                } else {
                    if (rowItem.orderCount === 0) {
                        check = false;
                    }
                }
            }
        }

        if (isN2GIntegration && errorsN2G) {
            if (errorsN2G.items && errorsN2G.items.length > 0) {
                check = false;
            }

            if (errorsN2G.location && errorsN2G.location.length > 0) {
                check = false;
            }

            if (errorsN2G.customer && errorsN2G.customer.length > 0) {
                check = false;
            }
        }

        if (check === true) {
            setIsLoading(true);
            const resolvedOrganizationVendorId = organizationVendorId || targetPurchaseOrder?.organizationVendorId || '';
            let payload: any;
            if (targetPurchaseOrder.id !== '') {
                payload = {
                    name: targetPurchaseOrder.name,
                    locationId: locationId,
                    location: {id: locationId},
                    organizationVendorId: resolvedOrganizationVendorId,
                    status: 'Requested',
                    requestedOn: new Date(),
                    requestedBy: userInfo.id,
                    requestedUser: {id: userInfo.id},
                    purchaseOrderItems: items.map((i: any) => {
                        return {
                            id: i.id,
                            itemId: i.itemId,
                            quantityOrdered: i.orderCount,
                            quantityReceived: 0
                        }
                    })
                }
            } else {
                payload = {
                    name: `Order By ${userInfo.firstName} ${userInfo.lastName}`,
                    locationId: locationId,
                    location: {id: locationId},
                    organizationVendorId: resolvedOrganizationVendorId,
                    status: 'Requested',
                    requestedOn: new Date(),
                    requestedBy: userInfo.id,
                    requestedUser: {id: userInfo.id},
                    purchaseOrderItems: items.map((i: any) => {
                        return {itemId: i.itemId, quantityOrdered: i.orderCount, quantityReceived: 0};
                    }),
                };
            }

            const checkMaxOrder = items?.filter((x: any) => x.item?.maxOrderQuantity && x.orderCount > x.item?.maxOrderQuantity);
            if (checkMaxOrder && checkMaxOrder?.length > 0) {
                const message: string = t('orderPage.updateCasesField');
                toast.error(message);
                setIsLoading(false);
            } else if (isN2GIntegration) {
                //validate data to n2g
                getLocationAndConvertLocationN2G(locationId).then((resLocation: any) => { 
                    const validateData: orderN2GProps = {
                        order: {
                            customer: getCustomerN2G(),
                            location: resLocation,
                            items: convertItemToItemN2G(items)
                        }
                    };
                    if (validateData.order?.location) {
                        const url = n2gValidateUrl;
                        axios.post(url, validateData, {headers: {'Access-Control-Allow-Origin': url, 'Accept': '*/*'}})
                            .then(async response => {
                                const res = targetPurchaseOrder.id !== '' ? await updatePurchaseOrderById(targetPurchaseOrder.id, payload) : await createPurchaseOrder(payload);
                                if (res.status >= apiStatus.SUCCESS) {
                                    const message: string = targetPurchaseOrder.id !== '' ? t('orderPage.editSuccess') : t('orderPage.saveSuccess');
                                    toast.success(message);
                                    fetchCurrentUser();
                                    setIsLoading(false);
                                    navigate(routePaths.PURCHASE_ORDER_PAGE);
                                } else {
                                    toast.error(res.statusText);
                                    setIsLoading(false);
                                    fetchCurrentUser();
                                }
                            })
                            .catch(error => {
                                if (error?.response?.data?.errors || error?.response?.data?.location) {
                                    const errorsItems: any = [];
                                    for (let index = 0; index < error.response.data?.errors?.length; index++) {
                                        const element = error.response.data?.errors[index];
                                        for (let index = 0; index < element.errors?.length; index++) {
                                            const elementMsg = element.errors[index];
                                            elementMsg.sku = element.sku;
                                            if (element.quantityAvailable !== undefined) {
                                                elementMsg.quantityAvailable = element.quantityAvailable;
                                            }
                                            errorsItems.push(elementMsg);
                                        }
                                    }
                                    const errorsLocation = error.response.data?.location?.errors;
                                    const errorsCustomer = error.response.data?.customer?.errors;
                                    const errorsList: any = {
                                        location: errorsLocation,
                                        items: errorsItems,
                                        customer: errorsCustomer
                                    }
                                    setErrorsN2G(errorsList);
                                    setIsLoading(false);
                                }
                            });
                    }
                });
            } else {
                try {
                    const res = targetPurchaseOrder.id !== '' ? await updatePurchaseOrderById(targetPurchaseOrder.id, payload) : await createPurchaseOrder(payload);
                    if (res.status >= apiStatus.SUCCESS) {
                        const message: string = targetPurchaseOrder.id !== '' ? t('orderPage.editSuccess') : t('orderPage.saveSuccess');
                        toast.success(message);
                        fetchCurrentUser();
                        setIsLoading(false);
                        navigate(routePaths.PURCHASE_ORDER_PAGE);
                    } else {
                        toast.error(res.statusText);
                        setIsLoading(false);
                        fetchCurrentUser();
                    }
                } catch (error: any) {
                    const hasMappedDotfitError = handleDotfitSubmitError(error);
                    if (!hasMappedDotfitError) {
                        const detail = error?.response?.data?.errors?.[0]?.detail;
                        toast.error(detail || t('orderPage.editFail'));
                    }
                    setIsLoading(false);
                }
            }
        } else {
            if (isN2GIntegration && errorsN2G?.location && errorsN2G?.location?.length > 0) {
                toast.error(`Location Error: ${buildErrorMessageN2G(errorsN2G.location)}`);
            }
            if (isN2GIntegration && errorsN2G?.customer && errorsN2G?.customer?.length > 0) {
                toast.error(`Location Error: ${buildErrorMessageN2G(errorsN2G.customer)}`);
            }
            toast.error(`${t('stockPage.requiredFieldMessage')}`);
            setIsLoading(false);
        }
    };

    const handleSaveDraft = async (items: any) => {
        if (hasDuplicateItemInRows(items)) {
            const message: string = t('orderPage.duplicateLineItemNotAllowed');
            toast.error(message);
            return;
        }
        setIsLoading(true);
        const resolvedOrganizationVendorId = organizationVendorId || targetPurchaseOrder?.organizationVendorId || '';
        let payload: any;
            if (targetPurchaseOrder.id !== '') {
                payload = {
                    name: targetPurchaseOrder.name,
                    locationId: locationId,
                    location: {id: locationId},
                    organizationVendorId: resolvedOrganizationVendorId,
                    status: 'Draft',
                    purchaseOrderItems: items.map((i: any) => {
                        return {
                            id: i.id,
                            itemId: i.itemId,
                            quantityOrdered: i.orderCount,
                            quantityReceived: 0
                        }
                    })
                }
            } else {
                payload = {
                    name: `Order By ${userInfo.firstName} ${userInfo.lastName}`,
                    locationId: locationId,
                    location: {id: locationId},
                    organizationVendorId: resolvedOrganizationVendorId,
                    status: 'Draft',
                    purchaseOrderItems: items.map((i: any) => {
                        return {itemId: i.itemId, quantityOrdered: i.orderCount, quantityReceived: 0};
                    }),
                };
            }

            const res = targetPurchaseOrder.id !== '' ? await updatePurchaseOrderById(targetPurchaseOrder.id, payload) : await createPurchaseOrder(payload);
            if (res.status >= apiStatus.SUCCESS) {
                const message: string = targetPurchaseOrder.id !== '' ? t('orderPage.editSuccess') : t('orderPage.saveSuccess');
                toast.success(message);
                fetchCurrentUser();;
                setIsLoading(false);
                navigate(-1);
            } else {
                toast.error(res.statusText);
                fetchCurrentUser();
                setIsLoading(false);
            }
    };

    const handleCancelOrder = async () => {
        if (targetPurchaseOrder && targetPurchaseOrder.id) {
            const response = await deletePurchaseOrderById(targetPurchaseOrder.id);
            if (response.status === 200) { 
                navigate(routePaths.PURCHASE_ORDER_PAGE);
            }
        } else {
            navigate(routePaths.PURCHASE_ORDER_PAGE);
        }
    }

    const handleAddRow = (item: any) => {
        const newRow: purchaseOrderItemType = {
            id: uuidv4(),
            itemId: '',
            item: null,
            orderCount: 1,
            quantityOnHand: 0,
            quantityOnOrder: 0,
            unitSoldLast7: 0,
            sixWeekAvg: 0,
            minimumStock: null,
        };
        const newData: any = concat(data, newRow);
        setData(newData);
    };

    const handleAddItemByCategory = () => {
        setOpenModal(!openModal);
    };

    const handleDuplicatePO = async (payload: any) => {
        if (payload?.purchaseOrderItems?.length > 0) {
            setIsLoading(true);
            const request = {
                name: `Order By ${userInfo.firstName} ${userInfo.lastName} - Duplicate`,
                locationId: payload.locationId,
                location: {id: payload.locationId},
                organizationVendorId: payload.organizationVendorId || targetPurchaseOrder?.organizationVendorId || organizationVendorId || '',
                status: 'Draft',
                purchaseOrderItems: payload?.purchaseOrderItems?.map((i: any) => {
                    return {itemId: i.itemId, quantityOrdered: i.quantityOrdered, quantityReceived: 0};
                }),
            };

            const res = await createPurchaseOrder(request);
            if (res.status >= apiStatus.SUCCESS) {
                setIsLoading(false);
                const message: string = t('orderPage.duplicateSuccess');
                toast.success(message);
                navigate(`${routePaths.PURCHASE_ORDER_DETAIL}/${res.data?.id}`);
            } else {
                setIsLoading(false);
                toast.error(`${t('orderPage.duplicateFail')}`);
            }
        }
    }

    const handleRemoveAllInvalid = async () => {
        if (!isN2GIntegration) {
            removeInActiveItem();
            return;
        }
        if (errorsN2G) {
            if (data && data.length > 0) {
                const listContentClone = cloneDeep(data);
                const validateData: orderN2GProps = {
                    order: {
                        customer: getCustomerN2G(),
                        location: await getLocationAndConvertLocationN2G(targetPurchaseOrder?.locationId),
                        items: convertItemToItemN2G(listContentClone)
                    }
                };
                if (validateData.order?.location) {
                    const url = n2gValidateUrl;
                    await axios.post(url, validateData, {headers: {'Access-Control-Allow-Origin': url, 'Accept': '*/*'}})
                        .then(response => {
                            setErrorsN2G(null);
                            removeInActiveItem();
                        })
                        .catch(error => {
                            if (error?.response?.data?.errors || error?.response?.data?.location) {
                                const errorsItems: any = [];
                                for (let index = 0; index < error.response.data?.errors?.length; index++) {
                                    const element = error.response.data?.errors[index];
                                    for (let index = 0; index < element.errors?.length; index++) {
                                        const elementMsg = element.errors[index];
                                        elementMsg.sku = element.sku;
                                        if (element.quantityAvailable !== undefined) {
                                            elementMsg.quantityAvailable = element.quantityAvailable;
                                        }
                                        errorsItems.push(elementMsg);
                                    }
                                }
                                const errorsLocation = error.response.data?.location?.errors;
                                const errorsCustomer = error.response.data?.customer?.errors;
                                const errorsList: any = {
                                    location: errorsLocation,
                                    items: errorsItems,
                                    customer: errorsCustomer
                                }
                                const skuItems: any = errorsItems?.map((x: any) => x.sku);
                                removeItemsBy('item.sku', skuItems);
                                setErrorsN2G(null);
                            }
                        });
                }
            }
        } else {
            removeInActiveItem();
        }
    };

    const addRowFunc = () => {
        let check = true;
        for (let index = 0; index < data.length; index++) {
            const rowItem: any = data[index];
            if (rowItem) {
                if (rowItem.itemId === '') {
                    check = false;
                } else {
                    if (rowItem.orderCount === 0) {
                        check = false;
                    }
                }
            }
        }

        if (check === false) {
            toast.error(`${t('stockPage.requiredFieldMessage')}`);
        } else {
            const newRow: purchaseOrderItemType = {
                id: uuidv4(),
                itemId: '',
                item: null,
                orderCount: 1,
                quantityOnHand: 0,
                quantityOnOrder: 0,
                unitSoldLast7: 0,
                sixWeekAvg: 0,
                minimumStock: null,
            };
            const newData: any = concat(data, newRow);
            setData(newData);
        }
    };

    const handleDeleteRow = (id: string, sku: string) => {
        const newData = data?.filter((d: any) => d.id !== id);
        setData(newData);
        let sum = 0;
        for (let index = 0; index < newData.length; index++) {
            const element: any = newData[index];
            sum = sum + ((element.orderCount || 0) * (element.item?.caseCost || 0));
        }
        targetPurchaseOrder.orderTotal = sum;
        setTotalItem(newData?.length);
        const newItemsErrorsN2G = errorsN2G?.items?.filter((e: any) => e.sku !== sku);
        const newErrorsN2G = {
            location: errorsN2G?.location,
            items: newItemsErrorsN2G
        }
        setErrorsN2G(newErrorsN2G);
    };

    const getStatisticsCountSafe = async (itemId: string, targetLocationId: string, numDay: number): Promise<number> => {
        try {
            const res = await statisticsSaleItem(itemId, targetLocationId, numDay);
            return res?.data?.count || 0;
        } catch (error) {
            return 0;
        }
    };

    const getQuantityOnHandSafe = async (targetLocationId: string, itemId: string): Promise<number> => {
        try {
            const resStock = await getStockByItemAndLocation(targetLocationId, itemId);
            return resStock?.data?.quantityOnHand || 0;
        } catch (error) {
            return 0;
        }
    };

    const getQuantityOnOrderSafe = async (targetLocationId: string, itemId: string): Promise<number> => {
        try {
            const resStock = await getStockByItemAndLocation(targetLocationId, itemId);
            return resStock?.data?.quantityOnOrder ?? 0;
        } catch (error) {
            return 0;
        }
    };

    const getMinimumStockSafe = async (targetLocationId: string, itemId: string): Promise<number | null> => {
        try {
            const resStock = await getStockByItemAndLocation(targetLocationId, itemId);
            return resStock?.data?.minimumStock ?? null;
        } catch (error) {
            return null;
        }
    };

    const handleStatictisItem = async (id: string, name: string) => {
        const chartCount = await getStatisticsCountSafe(id, locationId, 28);
        setDataChart({ count: chartCount });
        setItemNameChart(name);
        setOpenModalStatistics(!openModalStatistics);
    };

    const isDuplicateItemInAnotherRow = (rows: any[], currentRowId: string, itemId: string) =>
        rows.some((row: any) => row.id !== currentRowId && row.itemId === itemId);

    const hasDuplicateItemInRows = (rows: any[]) => {
        const selectedItemIds = rows
            .map((row: any) => row.itemId)
            .filter((itemId: string) => !!itemId);

        return new Set(selectedItemIds).size !== selectedItemIds.length;
    };

    const getItemOptionsForRow = (currentRowId: string) => {
        const selectedItemIds = new Set(
            data
                .filter((row: any) => row.id !== currentRowId && !!row.itemId)
                .map((row: any) => row.itemId),
        );

        return itemTypeOptions.filter((option: any) => {
            const optionItemId = option?.value?.id;
            return !optionItemId || !selectedItemIds.has(optionItemId);
        });
    };

    const handleChangeItem = async (id: string, option: any, field: any) => {
        const listContentClone = cloneDeep(data);
        const selectedItemId = option ? option.value?.id : '';
        if (field === 'itemId' && selectedItemId && isDuplicateItemInAnotherRow(listContentClone, id, selectedItemId)) {
            const message: string = t('orderPage.duplicateLineItemNotAllowed');
            toast.error(message);
            return;
        }
        const findContent: any = listContentClone?.find((item: any) => item.id === id);
        findContent[field] = selectedItemId;
        const res = await getItemById(selectedItemId);
        if (res.data) {
            findContent['item'] = res.data;
            findContent['orderCount'] = 1;
        }

        findContent["quantityOnHand"] = await getQuantityOnHandSafe(locationId, option ? option.value?.id : '');
        findContent["quantityOnOrder"] = await getQuantityOnOrderSafe(locationId, option ? option.value?.id : '');
        findContent["minimumStock"] = await getMinimumStockSafe(locationId, option ? option.value?.id : '');
        findContent["unitSoldLast7"] = await getStatisticsCountSafe(option ? option.value?.id : '', locationId, 7);
        findContent["sixWeekAvg"] = await getStatisticsCountSafe(option ? option.value?.id : '', locationId, 42);
        
        setData(listContentClone);
        if (isDotfitIntegration && errorsN2G?.items?.length > 0) {
            setErrorsN2G(null);
        }
        let sum = 0;
            for (let index = 0; index < listContentClone.length; index++) {
                const element: any = listContentClone[index];
                sum = sum + ((element.orderCount || 0) * (element.item?.caseCost || 0));
            }
        if (params?.id !== 'blank-new') {
            targetPurchaseOrder.orderTotal = sum;
        } else {
            
            setOrderTotalNew(sum);
        }
        setTotalItem(listContentClone?.length);

        if (!isN2GIntegration) return;

        const validateData: orderN2GProps = {
            order: {
                customer: getCustomerN2G(),
                location: await getLocationAndConvertLocationN2G(locationId),
                items: convertItemToItemN2G(listContentClone)
            }
        };
        if (validateData.order?.location) {
            const url = n2gValidateUrl;
            axios.post(url, validateData, {headers: {'Access-Control-Allow-Origin': url, 'Accept': '*/*'}})
                .then(response => {
                    setErrorsN2G(null);
                })
                .catch(error => {
                    if (error?.response?.data?.errors || error?.response?.data?.location) {
                        const errorsItems: any = [];
                        for (let index = 0; index < error.response.data?.errors?.length; index++) {
                            const element = error.response.data?.errors[index];
                            for (let index = 0; index < element.errors?.length; index++) {
                                const elementMsg = element.errors[index];
                                elementMsg.sku = element.sku;
                                if (element.quantityAvailable !== undefined) {
                                    elementMsg.quantityAvailable = element.quantityAvailable;
                                }
                                errorsItems.push(elementMsg);
                            }
                        }
                        const errorsLocation = error.response.data?.location?.errors;
                        const errorsCustomer = error.response.data?.customer?.errors;
                        const errorsList: any = {
                            location: errorsLocation,
                            items: errorsItems,
                            customer: errorsCustomer
                        }
                        setErrorsN2G(errorsList);
                    }
                });
        }
    };

    const handleChangeContentOverview = async (id: string, value: any, field: any) => {
        const listContentClone = cloneDeep(data);
        const findContent: any = listContentClone?.find((item: any) => item.id === id);
        if (field === 'orderCount') {
            const item = findContent['item'];
            findContent[field] = !isEmpty(value) ? parseInt(value) : 0;
        }
        setData(listContentClone);
        if (isDotfitIntegration && errorsN2G?.items?.length > 0) {
            setErrorsN2G(null);
        }
        let sum = 0;
            for (let index = 0; index < listContentClone.length; index++) {
                const element: any = listContentClone[index];
                sum = sum + ((element.orderCount || 0) * (element.item?.caseCost || 0));
            }
        if (params?.id !== 'blank-new') {
            targetPurchaseOrder.orderTotal = sum;
        } else {
            
            setOrderTotalNew(sum);
        }
        setTotalItem(listContentClone?.length);

        if (!isN2GIntegration) return;

        const validateData: orderN2GProps = {
            order: {
                customer: getCustomerN2G(),
                location: await getLocationAndConvertLocationN2G(locationId),
                items: convertItemToItemN2G(listContentClone)
            }
        };
        if (validateData.order?.location) {
            const url = n2gValidateUrl;
            axios.post(url, validateData, {headers: {'Access-Control-Allow-Origin': url, 'Accept': '*/*'}})
                .then(response => {
                    setErrorsN2G(null);
                })
                .catch(error => {
                    if (error?.response?.data?.errors || error?.response?.data?.location) {
                        const errorsItems: any = [];
                        for (let index = 0; index < error.response.data?.errors?.length; index++) {
                            const element = error.response.data?.errors[index];
                            for (let index = 0; index < element.errors?.length; index++) {
                                const elementMsg = element.errors[index];
                                elementMsg.sku = element.sku;
                                if (element.quantityAvailable !== undefined) {
                                    elementMsg.quantityAvailable = element.quantityAvailable;
                                }
                                errorsItems.push(elementMsg);
                            }
                        }
                        const errorsLocation = error.response.data?.location?.errors;
                        const errorsCustomer = error.response.data?.customer?.errors;
                        const errorsList: any = {
                            location: errorsLocation,
                            items: errorsItems,
                            customer: errorsCustomer
                        }
                        setErrorsN2G(errorsList);
                    }
                });
        }
    };

    const getItemSkuForVendor = (item: any) => {
        const vendorId = item?.organizationVendorId;
        const vendorSku = vendorId ? item?.vendorConfig?.[vendorId]?.vendorSku : '';
        return (vendorSku || '').trim() || item?.sku;
    };

    const convertItemToItemN2G = (items: any) => {
        return items?.map((x: any) => {
            return {
                sku: getItemSkuForVendor(x.item),
                quantity: x.orderCount || x.quantityOrdered || 0
            }
        });
    };

    const getLocationAndConvertLocationN2G = async (locationId: string) => {
        const res = await getLocationById(locationId);
        const location = res?.data;

        const {NODE_ENV} = process.env;

        return {
            name: NODE_ENV === "production" ? `${location?.organization?.name} ${location?.clubCode}` : `[TEST] ${location?.organization?.name} ${location?.clubCode}`,
            clubCode: location?.clubCode,
            address: location?.streetAddress1,
            address2: location?.streetAddress2,
            city: location?.city,
            state: location?.state,
            zip: location?.zipCode,
            country: "United States"
        }
    };

    const getCustomerN2G = () => {
        return {
            name: targetPurchaseOrder?.requestedUser ? `${targetPurchaseOrder.requestedUser?.firstName} ${targetPurchaseOrder.requestedUser?.lastName}` : `${userInfo.firstName} ${userInfo.lastName}`,
            email: targetPurchaseOrder?.requestedUser ? targetPurchaseOrder.requestedUser?.emailAddress : userInfo.emailAddress
        }
    }

    const getCustomerN2GWhenLoading = (targetPo: any) => {
        return {
            name: targetPo?.requestedUser ? `${targetPo.requestedUser?.firstName} ${targetPo.requestedUser?.lastName}` : `${userInfo.firstName} ${userInfo.lastName}`,
            email: targetPo?.requestedUser ? targetPo.requestedUser?.emailAddress : userInfo.emailAddress
        }
    }

    const buildErrorMessageN2G = (errors: any) => {

        let message = '';
        for (let index = 0; index < errors.length; index++) {
            const element = errors[index];
            if (message === '') {
                message = element?.message;
            } else {
                message += `, ${element.message}`;
            }
        }
        ;

        return message;
    };

    const parseDotfitInventoryError = (detail: string) => {
        const insufficientInventoryPattern = /Insufficient DotFit inventory for "(.*)" \((.*)\)\. Requested (\d+), available (\d+)\./;
        const unavailableInventoryPattern = /Item "(.*)" \((.*)\) is unavailable in DotFit inventory/;

        const insufficientMatch = detail.match(insufficientInventoryPattern);
        if (insufficientMatch) {
            return {
                sku: insufficientMatch[2],
                quantityAvailable: Number(insufficientMatch[4]),
                message: '',
            };
        }

        const unavailableMatch = detail.match(unavailableInventoryPattern);
        if (unavailableMatch) {
            return {
                sku: unavailableMatch[2],
                message: detail,
            };
        }

        return null;
    };

    const handleDotfitSubmitError = (error: any) => {
        if (!isDotfitIntegration) return false;
        const detail = error?.response?.data?.errors?.[0]?.detail;
        if (!detail || typeof detail !== 'string') return false;

        const parsedError = parseDotfitInventoryError(detail);
        if (!parsedError) return false;

        setErrorsN2G({
            location: null,
            items: [parsedError],
            customer: null,
        });
        return true;
    };

    const getErrorItemN2G = (sku: string) => {
        if (!isN2GIntegration && !isDotfitIntegration) return '';
        let message = '';
        const errorItemBySku = errorsN2G?.items?.find((i: any) => i.sku === sku);
        if (errorItemBySku) {
            message = errorItemBySku.quantityAvailable !== undefined ? t('orderPage.itemN2GOutOfStock').replace('%quantity%', errorItemBySku.quantityAvailable) : errorItemBySku.message;
        }

        return message;
    }

    const customFilterOption = (option: any, inputValue: any) => {
        const label = option.label?.toLowerCase();
        const upc = option.value?.upcs?.find((x: any) => x?.upcCode?.includes(inputValue))?.upcCode?.toLowerCase();
        const input = inputValue?.toLowerCase();
    
        return label?.includes(input) || upc?.includes(input);
    };

    //remove inactive item
    const removeInActiveItem = () => {
        setData(
            data.filter((entry: any) => entry.item?.isActive === true)
        );
    };
    // Generic function to remove items by a property
    const removeItemsBy = (property: string, valuesToRemove: any) => {
        const removeInactiveData = data.filter((entry: any) => entry.item?.isActive === true);
        setData(
            removeInactiveData.filter((entry) => {
            const row: any = entry;
            // Handle nested properties (e.g., item.id, item.sku)
            const value = property === 'item.sku'
            ? getItemSkuForVendor(row.item)
            : property.includes('.')
                ? property.split('.').reduce((obj: any, key: any) => obj[key], row)
                : row[property];
            return !valuesToRemove.includes(value);
        })
        );
    };

    useEffect(() => {
        if (params.id !== 'blank-new') {
            setHasInitializedBlankOrderRow(false);
            return;
        }
        if (data.length !== 0) return;
        if (hasInitializedBlankOrderRow) return;

        handleAddRow(null);
        setHasInitializedBlankOrderRow(true);

        if (shouldAutoAddItemRow) {
            navigate(`${location.pathname}${location.search}`, {replace: true, state: {}});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.id, shouldAutoAddItemRow, data.length, hasInitializedBlankOrderRow]);
    

    return (
        <>
            <div className="flex flex-col w-full" id="purchase-order-detail-custom-portal">
                <div className="flex relative xl:flex-row gap-4 flex-col w-full p-[15px] border-b border-gray-200 justify-between">
                    <ButtonBack buttonName={t('sidebar.purchaseOrder')} handleBack={() => handleCancelOrder()}/>
                    <div className="text-primary text-lg font-semibold flex items-center">
                {params.id !== 'blank-new'
                ?
                    <>
                        <div className="text-lg">
                            <span className="gr">
                                {selectedVendorName ? <span>{selectedVendorName}&nbsp;|&nbsp;</span> : null}
                                <span className="font-normal">
                                    {t('orderPage.orderId').toUpperCase()}: &nbsp;
                                </span>
                                <span>{targetPurchaseOrder.vendorOrderId ? targetPurchaseOrder.vendorOrderId : targetPurchaseOrder.id}&nbsp;</span>
                                <span className="font-normal">({new Intl.NumberFormat('en-US').format(
                                    totalItem,
                                )} items)</span>&nbsp;| &nbsp;
                            </span>
                            <span className="gr">
                                <span className="font-normal">
                                    {t('orderPage.total').toUpperCase()}:
                                </span>
                                <span>{formatMoney(targetPurchaseOrder.orderTotal || 0)}</span>
                            </span>
                        </div>
                    </>
                : 
                `${selectedVendorName ? `${t('orderPage.newPurchaseOrder')} for ${selectedVendorName}` : t('orderPage.newPurchaseOrder')} | ${t('orderPage.total').toUpperCase()}: ${formatMoney(orderTotalNew || 0)}`}
                </div>
                    <div className="flex md:gap-4 gap-2">
                    {
                        params.id !== 'blank-new' && targetPurchaseOrder?.status !== "Draft" 
                        && (<Buttons className="text-white bg-yellow-500" onClick={() => isLoading ? {} : handleDuplicatePO(targetPurchaseOrder)}
                                                 text={t('orderPage.duplicated')}/>)
                    }
                    {isN2GIntegration && (
                        <Buttons className="text-white bg-red-500 w-[172px]" onClick={() => handleRemoveAllInvalid()}
                                                 text={t('orderPage.removeAllInvalid')}/>
                    )}
                </div>
            </div>
                <>
                    {data && data.length === 0 ? (
                        <EmptyState
                            buttonName={t('itemPage.addItem')}
                            handleAddClick={isWriteOrg && handleAddRow}
                            title={t('stockPage.titleNoItemResult')}
                            subtitle={t('itemPage.subTitleNoResult')}
                            icon={itemEmpty}
                        />
                    ) : (
                        <div className="purchase-order-detail overflow-x-auto">
                            <Table>
                                <Table.Head className="text-gray-900 border-b border-gray-100 border-b-2 w-full">
                                    <Table.HeadCell
                                        className="cursor-pointer bg-white font-medium text-xs item px-0 pr-3 py-2"
                                        onClick={() => handleHeaderSort('itemName')}>
                                            <div className="flex items-center ">
                                                {t('itemPage.item')}
                                                <Sort check={sortBy === 'itemName'} sortByType={sortByType}/>
                                            </div>
                                    </Table.HeadCell>
                                    <Table.HeadCell
                                        className="cursor-pointer bg-white font-medium text-xs upc px-3 py-0"
                                        onClick={() => handleHeaderSort('itemSku')}>
                                            <div className="flex items-center ">
                                                {t('itemPage.sku').toUpperCase()}
                                                <Sort check={sortBy === 'itemSku'} sortByType={sortByType}/>
                                            </div>
                                    </Table.HeadCell>
                                    <Table.HeadCell
                                        className="cursor-pointer bg-white font-medium text-xs unit-pc px-3 py-0">
                                        {t('stockPage.count').toLocaleUpperCase()}
                                    </Table.HeadCell>
                                    <Table.HeadCell
                                        className="cursor-pointer bg-white font-medium text-xs unit-pc px-3 py-0">
                                        {t('orderPage.unitSoldLast7').toLocaleUpperCase()}
                                    </Table.HeadCell>
                                                    <Table.HeadCell
                                                        className="cursor-pointer bg-white font-medium text-xs unit-pc px-3 py-0">
                                                        {t('orderPage.6weekavg').toLocaleUpperCase()}
                                                    </Table.HeadCell>
                                                    <Table.HeadCell
                                                        className="cursor-pointer bg-white font-medium text-xs unit-pc px-3 py-0">
                                                        MIN STOCK
                                                    </Table.HeadCell>
                                                    <Table.HeadCell
                                                        className="cursor-pointer bg-white font-medium text-xs unit-pc px-3 py-0">
                                                        {t('orderPage.incomingOrderQty').toLocaleUpperCase()}
                                                    </Table.HeadCell>
                                    <Table.HeadCell
                                        className="cursor-pointer bg-white font-medium text-xs cases px-3 py-0"
                                        onClick={() => handleHeaderSort('quantityOrdered')}>
                                            <div className="flex items-center ">
                                                {t('orderPage.cases').toUpperCase()}
                                                <Sort check={sortBy === 'quantityOrdered'} sortByType={sortByType}/>
                                            </div>
                                    </Table.HeadCell>
                                    <Table.HeadCell
                                        className="cursor-pointer bg-white font-medium text-xs cost-pc px-3 py-0"
                                        onClick={() => handleHeaderSort('itemCaseCost')}>
                                            <div className="flex items-center ">
                                                {t('orderPage.costPerCase').toLocaleUpperCase()}
                                                <Sort check={sortBy === 'itemCaseCost'} sortByType={sortByType}/>
                                            </div>
                                    </Table.HeadCell>
                                    <Table.HeadCell
                                        className="cursor-pointer bg-white font-medium text-xs unit-pc px-3 py-0">
                                        {t('orderPage.unitPerCase').toLocaleUpperCase()}
                                    </Table.HeadCell>
                                    <Table.HeadCell
                                        className="cursor-pointer bg-white font-medium text-xs total px-3 py-0">
                                        {t('orderPage.totalUnit').toLocaleUpperCase()}
                                    </Table.HeadCell>
                                    <Table.HeadCell
                                        className="cursor-pointer bg-white font-medium text-xs order px-3 py-0">{t('orderPage.totalOrder')}</Table.HeadCell>
                                    <Table.HeadCell
                                        className=" bg-white font-medium text-xs action px-3 py-0">{''}</Table.HeadCell>
                                </Table.Head>
                                <Table.Body
                                    // className={cn('', {
                                    //   '--overflow': data?.length > 6,
                                    // })}
                                >
                                    {data.map((item: purchaseOrderItemType, index) => {
                                        return (
                                            <Table.Row key={`row-${item.id}`}
                                                       className="cursor-pointer bg-white text-sm hover:bg-gray-50 border-b border-gray-100">
                                                {item.itemId === '' ?
                                                    (
                                                        <Table.Cell className="px-0 pr-3 py-3 item">
                                                            <MultiSelect
                                                                onChange={(e: any) => {
                                                                    handleChangeItem(item?.id, e, 'itemId');
                                                                }}
                                                                options={getItemOptionsForRow(item.id)}
                                                                value={itemTypeOptions.find((i: any) => i.value?.id === item.itemId)}
                                                                id={`item-${index}`}
                                                                name={`item-${index}`}
                                                                noOptionsMessage={() => t('No options')}
                                                                closeMenuOnSelect
                                                                isHolderLabel={false}
                                                                isDisabled={
                                                                    targetPurchaseOrder.status === 'Ordered' ||
                                                                    targetPurchaseOrder.status === 'Rejected' ||
                                                                    targetPurchaseOrder.status === 'Received'
                                                                        ? true
                                                                        : false
                                                                }
                                                                filterOption={customFilterOption}
                                                                dropdownRoot={document.getElementById('purchase-order-detail-custom-portal')}

                                                            />
                                                            <div
                                                                className={`text-red-600 text-xs font-normal mt-1 warning`}>{t('userManagementPage.requiredField')}</div>
                                                        </Table.Cell>
                                                    ) :
                                                    (
                                                        <Tooltip
                                                            content={itemTypeOptions.find((i: any) => i.value?.id === item.itemId) ? itemTypeOptions.find((i: any) => i.value?.id === item.itemId)?.label : ""}>
                                                            <Table.Cell className="px-0 pr-3 py-3 item">
                                                                <MultiSelect
                                                                    onChange={(e: any) => {
                                                                        handleChangeItem(item?.id, e, 'itemId');
                                                                    }}
                                                                    options={getItemOptionsForRow(item.id)}
                                                                    value={itemTypeOptions.find((i: any) => i.value?.id === item.itemId)}
                                                                    id={`item-${index}`}
                                                                    name={`item-${index}`}
                                                                    noOptionsMessage={() => t('No options')}
                                                                    closeMenuOnSelect
                                                                    isHolderLabel={false}
                                                                    isDisabled={
                                                                        targetPurchaseOrder.status === 'Ordered' ||
                                                                        targetPurchaseOrder.status === 'Rejected' ||
                                                                        targetPurchaseOrder.status === 'Received'
                                                                            ? true
                                                                            : false
                                                                    }
                                                                    filterOption={customFilterOption}
                                                                    dropdownRoot={document.getElementById('purchase-order-detail-custom-portal')}
                                                                />
                                                            </Table.Cell>
                                                            {item.item?.isActive === false && (
                                                                <div className={`text-red-600 text-xs font-normal mt-1 warning`}>{t('itemPage.inactiveItem')}
                                                                </div>
                                                            )}
                                                        </Tooltip>
                                                    )
                                                }
                                                <Table.Cell className="px-3 py-3 upc">
                                                    <Label>{getItemSkuForVendor(item?.item)}</Label>
                                                    {getErrorItemN2G(getItemSkuForVendor(item?.item)) !== ''
                                                        ?
                                                        (<div
                                                            className={`text-red-600 text-xs font-normal mt-1 warning`}>{getErrorItemN2G(getItemSkuForVendor(item?.item))}</div>)
                                                        :
                                                        ('')
                                                    }
                                                </Table.Cell>
                                                <Table.Cell className="px-3 py-3 unit-pc">
                                                    <Label>{item?.quantityOnHand || 0}</Label>
                                                </Table.Cell>
                                                <Table.Cell className="px-3 py-3 unit-pc">
                                                    <Label>{item?.unitSoldLast7 || 0}</Label>
                                                </Table.Cell>
                                                <Table.Cell className="px-3 py-3 unit-pc">
                                                    <Label>{item?.sixWeekAvg || 0}</Label>
                                                </Table.Cell>
                                                <Table.Cell className="px-3 py-3 unit-pc">
                                                    <Label>{item?.minimumStock ?? '—'}</Label>
                                                </Table.Cell>
                                                <Table.Cell className="px-3 py-3 unit-pc">
                                                    <Label>{item?.quantityOnOrder ?? 0}</Label>
                                                </Table.Cell>
                                                <Table.Cell className="px-3 py-3 cases">
                                                    <TextInput
                                                        type="number"
                                                        min={1}
                                                        max={item?.item?.maxOrderQuantity || undefined}
                                                        className="custom-input-count"
                                                        id={`orderCount-${index}`}
                                                        name={`orderCount-${index}`}
                                                        value={item?.orderCount}
                                                        onChange={(e: any) => {
                                                            handleChangeContentOverview(item?.id, e.target.value, 'orderCount');
                                                        }}
                                                        disabled={
                                                            targetPurchaseOrder.status === 'Ordered' ||
                                                            targetPurchaseOrder.status === 'Rejected' ||
                                                            targetPurchaseOrder.status === 'Received'
                                                                ? true
                                                                : false
                                                        }
                                                    />
                                                    {item.orderCount === 0 ? (
                                                        <div
                                                            className={`text-red-600 text-xs font-normal mt-1 warning`}>{t('userManagementPage.requiredField')}</div>
                                                    ) : (
                                                        ''
                                                    )}
                                                    {item.item?.maxOrderQuantity && (item.orderCount || 0) > item.item?.maxOrderQuantity ? (
                                                        <div
                                                            className={`text-red-600 text-xs font-normal mt-1 warning`}>
                                                            Max Order: {item.item?.maxOrderQuantity}
                                                        </div>
                                                    ) : (
                                                        ''
                                                    )}
                                                </Table.Cell>
                                                <Table.Cell className="px-3 py-3 cost-pc">
                                                    <Label>{formatMoney(item?.item?.caseCost)}</Label>
                                                </Table.Cell>
                                                <Table.Cell className="px-3 py-3 unit-pc">
                                                    <Label>{item?.item?.upcs?.find((x: any) => x.default === true)?.caseQuantity}</Label>
                                                </Table.Cell>
                                                <Table.Cell className="px-3 py-3 total">
                                                    <Label>{item?.orderCount ? item?.orderCount * item?.item?.upcs?.find((x: any) => x.default === true)?.caseQuantity : 0}</Label>
                                                </Table.Cell>
                                                <Table.Cell className="px-3 py-3 order">
                                                    <Label>{formatMoney((item?.orderCount || 0) * (item?.item?.caseCost))}</Label>
                                                </Table.Cell>
                                                <Table.Cell className="px-3 py-3 action">
                                                    <GroupButtonWithIcons
                                                        className="items-center justify-center"
                                                        buttons={[
                                                            {
                                                                type: 'button',
                                                                text: '',
                                                                classType: 'white',
                                                                icon: <HiOutlineTrash size={'20px'}/>,
                                                                disabled:
                                                                    params.id === 'blank-new'
                                                                        ? false
                                                                        : targetPurchaseOrder.status === 'Ordered' ||
                                                                        targetPurchaseOrder.status === 'Rejected' ||
                                                                        targetPurchaseOrder.status === 'Received'
                                                                            ? true
                                                                            : false,
                                                                action: () => {
                                                                    handleDeleteRow(item?.id, getItemSkuForVendor(item?.item));
                                                                },
                                                            },
                                                            {
                                                                type: 'button',
                                                                text: '',
                                                                classType: 'white',
                                                                icon: <HiOutlineExclamationCircle size={'20px'}/>,
                                                                action: () => {
                                                                    handleStatictisItem(item?.item?.id, item?.item?.name);
                                                                },
                                                            }
                                                        ]}
                                                    />
                                                </Table.Cell>
                                            </Table.Row>
                                        );
                                    })}
                                </Table.Body>
                            </Table>
                            {params.id === 'blank-new' ? (
                                <div className="group-button-footer">
                                    <div className="left">
                                        <Buttons className="text-secondary border border-secondary"
                                                 onClick={() => addRowFunc()} text={t('orderPage.addSingleItem')}/>
                                        <Buttons
                                            className="text-secondary border border-secondary"
                                            onClick={() => handleAddItemByCategory()}
                                            text={t('orderPage.addItemBycategory')}
                                        />
                                    </div>
                                    <div
                                        className="right">
                                        <Buttons className="text-white bg-gray-400"
                                                 onClick={() => isLoading ? {} : handleSaveDraft(data)}
                                                 text={t('orderPage.saveDraft')}/>
                                        <Buttons className="text-white bg-primary"
                                                 onClick={() => isLoading ? {} : handleSubmitPurchaseOrder(data)}
                                                 text={t('orderPage.submitReview')}/>
                                        <Buttons className="text-white bg-red-500" onClick={() => handleCancelOrder()}
                                                 text={t('orderPage.cancel')}/>
                                    </div>
                                </div>
                            ) : targetPurchaseOrder.status === 'Requested' || targetPurchaseOrder.status === 'Draft' ? (
                                <div className="group-button-footer">
                                    <div className="left">
                                        <Buttons className="text-secondary border border-secondary"
                                                 onClick={() => addRowFunc()} text={t('orderPage.addSingleItem')}/>
                                        <Buttons
                                            className="text-secondary border border-secondary"
                                            onClick={() => handleAddItemByCategory()}
                                            text={t('orderPage.addItemBycategory')}
                                        />
                                    </div>
                                    <div
                                        className="right">
                                        <Buttons className="text-white bg-gray-400"
                                                 onClick={() => isLoading ? {} : handleSaveDraft(data)}
                                                 text={t('orderPage.saveDraft')}/>
                                        <Buttons className="text-white bg-primary"
                                                 onClick={() => isLoading ? {} : handleSubmitPurchaseOrder(data)}
                                                 text={t('orderPage.submitReview')}/>
                                        <Buttons className="text-white bg-red-500" onClick={() => handleCancelOrder()}
                                                 text={t('orderPage.cancel')}/>
                                    </div>
                                </div>
                            ) : (
                                ''
                            )}
                        </div>
                    )}
                </>
            </div>
            {openModal && (
                <CreateItemByCategoryModal
                    headerTitle={t('categoryPage.chooseCategory')}
                    openModal={openModal}
                    setOpenModal={setOpenModal}
                    category={category}
                    setCategory={setCategory}
                    queryClient={queryClient}
                    setCurrentPage={setCurrentPage}
                    currentPage={currentPage}
                />
            )}
            {openModalStatistics && (
                <StatisticsSaleModal
                    headerTitle={t('stockPage.statisticsSale')}
                    openModal={openModalStatistics}
                    setOpenModal={setOpenModalStatistics}
                    itemName={itemNameChart}
                    data={dataChart}
                />
            )}
        </>
    );
};

export default PurchaseOrderDetail;
